import { Store } from '@ngrx/store';
import { selectFirstCompanyByAccountId } from '@mkp/company/state';
import { selectAccountMemberships } from '@mkp/account-membership/state';
import { selectActiveAccounts } from '@mkp/account/state';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  DeprecatedGuard,
} from '@angular/router';
import { Type } from '@angular/core';
import { getComposedGuards } from '@mkp/shared/util-guards';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

export const getSelectWorkspacePayload = (accountId: string, store: Store) => {
  return combineLatest({
    accountId: findAccountId(accountId, store),
    companyProfileId: findCompanyProfileId(accountId, store),
    accountMembershipId: findAccountMembershipId(accountId, store),
  }).pipe(take(1));
};

const findCompanyProfileId = (accountId: string, store: Store) =>
  store.select(selectFirstCompanyByAccountId(accountId)).pipe(
    filter(Boolean),
    map((company) => company.id),
    take(1)
  );
const findAccountId = (accountId: string, store: Store) =>
  store.select(selectActiveAccounts).pipe(
    map((accounts) => accounts.find((account) => account.id === accountId)),
    map((account) => account?.id),
    filter(Boolean),
    take(1)
  );
const findAccountMembershipId = (accountId: string, store: Store) =>
  store.select(selectAccountMemberships).pipe(
    map((memberships) => memberships.find((membership) => membership.accountId === accountId)),
    map((membership) => membership?.id),
    filter(Boolean),
    take(1)
  );

export const isBoUserFn = (isBoUser: boolean | null) => isBoUser !== null && isBoUser;

export function isRouteProtectedByGuard(
  route: ActivatedRouteSnapshot,
  guards: (Type<unknown> | CanActivateFn | CanMatchFn | DeprecatedGuard)[]
): boolean {
  // Both matches are by reference, and a composed guard is only known as unknown, so the
  // haystack is widened once rather than each needle asserted back into the union.
  const listed: readonly unknown[] = guards;
  let current: ActivatedRouteSnapshot | null = route.firstChild;

  while (current) {
    const routeGuards =
      current.routeConfig?.canActivate ||
      current.routeConfig?.canMatch ||
      current.routeConfig?.canLoad ||
      [];
    if (
      routeGuards.some(
        (guard) =>
          listed.includes(guard) ||
          getComposedGuards(guard).some((composed) => listed.includes(composed))
      )
    ) {
      return true;
    }
    current = current.firstChild;
  }

  return false;
}
